/**
 * BattleCode Demo v1.0.0 — SplitPanel 拖拽分割器
 *
 * 纯 TypeScript 类，零依赖，通过 Pointer Events 实现面板拖拽。
 * 用于屏 4 战斗页面的柔性面板框架，模块间通过可拖拽分割线改变大小。
 *
 * 设计要点：
 *   - grip 固定 4px 宽/高
 *   - horizontal 模式改 width，vertical 模式改 height
 *   - panelA 用 flexBasis 固定尺寸，panelB 用 flex:1 自动填满
 *   - pointermove 使用 rAF 节流保证 60fps
 *   - 支持 minSizes 双向 clamp + defaultSizes 初始化
 */

export interface SplitPanelConfig {
  /** 父容器（flex 布局） */
  container: HTMLElement;
  /** 面板 A（拖拽左侧/上方面板） */
  panelA: HTMLElement;
  /** 面板 B（拖拽右侧/下方面板） */
  panelB: HTMLElement;
  /** 拖拽握柄元素 */
  grip: HTMLElement;
  /** 水平拖拽 = 改宽度，垂直拖拽 = 改高度 */
  direction: "horizontal" | "vertical";
  /** [面板A最小px, 面板B最小px] */
  minSizes: [number, number];
  /** [面板A默认px, 面板B默认px] */
  defaultSizes: [number, number];
  /** resize 回调，每次拖拽结束后触发 */
  onResize?: (sizes: [number, number]) => void;
}

export class SplitPanel {
  private container: HTMLElement;
  private panelA: HTMLElement;
  private panelB: HTMLElement;
  private grip: HTMLElement;
  private direction: "horizontal" | "vertical";
  private minSizes: [number, number];
  private defaultSizes: [number, number];
  private onResize?: (sizes: [number, number]) => void;

  // 拖拽中间状态
  private dragging = false;
  private startCoord = 0;
  private startSizeA = 0;
  private rafId: number | null = null;
  private bodyStyleSnapshot: { userSelect: string; cursor: string } | null = null;

  // 绑定的事件处理器引用（用于 destroy 时 removeEventListener）
  private boundOnPointerDown: (e: PointerEvent) => void;
  private boundOnPointerMove: (e: PointerEvent) => void;
  private boundOnPointerUp: (e: PointerEvent) => void;

  constructor(config: SplitPanelConfig) {
    this.container = config.container;
    this.panelA = config.panelA;
    this.panelB = config.panelB;
    this.grip = config.grip;
    this.direction = config.direction;
    this.minSizes = config.minSizes;
    this.defaultSizes = config.defaultSizes;
    this.onResize = config.onResize;

    // 绑定方法引用，保证 destroy 能正确移除
    this.boundOnPointerDown = this.onPointerDown.bind(this);
    this.boundOnPointerMove = this.onPointerMove.bind(this);
    this.boundOnPointerUp = this.onPointerUp.bind(this);

    this.init();
  }

  // ===== 初始化 =====

  private init(): void {
    // 1. 设置默认尺寸
    this.applySizes(this.defaultSizes[0], this.defaultSizes[1]);

    // 2. grip 基础样式
    this.grip.style.flexShrink = "0";
    this.grip.style.position = "relative";

    if (this.direction === "horizontal") {
      this.grip.style.width = "4px";
      this.grip.style.cursor = "col-resize";
      // 扩大热区：通过伪元素不可行（内联样式），用透明 padding 或内部 span
      // 这里用最小侵入方案：给 grip 自身加 padding 扩大可点击区域
    } else {
      this.grip.style.height = "4px";
      this.grip.style.cursor = "row-resize";
    }

    // 3. panelB 保持 flex:1 自动填满
    this.panelB.style.flex = "1";

    // 4. hover 样式（通过类名控制，避免直接操作 style 覆盖用户样式）
    this.grip.addEventListener("pointerenter", this.onPointerEnter);
    this.grip.addEventListener("pointerleave", this.onPointerLeave);

    // 5. 拖拽入口
    this.grip.addEventListener("pointerdown", this.boundOnPointerDown);
  }

  // ===== Hover 样式 =====

  private onPointerEnter = (): void => {
    this.grip.style.backgroundColor = "rgba(0, 228, 255, 0.6)";
  };

  private onPointerLeave = (): void => {
    // 拖拽中不重置 hover 样式
    if (this.dragging) return;
    this.grip.style.backgroundColor = "";
  };

  // ===== Pointer Events =====

  private onPointerDown(e: PointerEvent): void {
    e.preventDefault();

    this.dragging = true;
    this.grip.setPointerCapture(e.pointerId);

    // 记录初始状态
    const rectA = this.panelA.getBoundingClientRect();
    if (this.direction === "horizontal") {
      this.startCoord = e.clientX;
      this.startSizeA = rectA.width;
    } else {
      this.startCoord = e.clientY;
      this.startSizeA = rectA.height;
    }

    // 全局样式：禁止选中 + 强制拖拽光标；保存原值，避免路由离开后污染下一屏
    this.bodyStyleSnapshot = {
      userSelect: document.body.style.userSelect,
      cursor: document.body.style.cursor,
    };
    document.body.style.userSelect = "none";
    document.body.style.cursor = this.direction === "horizontal" ? "col-resize" : "row-resize";

    // 监听全局 move/up
    document.addEventListener("pointermove", this.boundOnPointerMove);
    document.addEventListener("pointerup", this.boundOnPointerUp);
  }

  private onPointerMove(e: PointerEvent): void {
    if (!this.dragging) return;

    // rAF 节流：如果已有待处理的帧，跳过
    if (this.rafId !== null) return;

    const currentCoord = this.direction === "horizontal" ? e.clientX : e.clientY;
    const delta = currentCoord - this.startCoord;
    const newSizeA = this.clampSizeA(this.startSizeA + delta);

    this.rafId = requestAnimationFrame(() => {
      this.rafId = null;
      this.applySizeA(newSizeA);
    });
  }

  private onPointerUp(e: PointerEvent): void {
    if (!this.dragging) return;

    this.dragging = false;
    this.grip.releasePointerCapture(e.pointerId);

    // 恢复拖拽前的全局样式
    this.restoreBodyStyles();

    // 取消可能残留的 rAF
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }

    // 移除全局监听
    document.removeEventListener("pointermove", this.boundOnPointerMove);
    document.removeEventListener("pointerup", this.boundOnPointerUp);

    // 触发回调
    const sizeA = this.getSizeA();
    const sizeB = this.getSizeB();
    this.onResize?.([sizeA, sizeB]);
  }

  private restoreBodyStyles(): void {
    if (!this.bodyStyleSnapshot) return;
    document.body.style.userSelect = this.bodyStyleSnapshot.userSelect;
    document.body.style.cursor = this.bodyStyleSnapshot.cursor;
    this.bodyStyleSnapshot = null;
  }

  // ===== 尺寸计算 =====

  /**
   * 将 panelA 的新尺寸 clamp 到 minSizes 范围内。
   * panelB 的最小尺寸通过 container 总尺寸 - panelA 最大尺寸来保证。
   */
  private clampSizeA(raw: number): number {
    const [minA, minB] = this.minSizes;

    // clamp 到 minA
    let clamped = Math.max(minA, raw);

    // 保证 panelB 至少有 minB（按 A/B 这一对面板的可分配空间计算）
    // 注意：pairSize 不含 grip，本处先减 grip 再减 minB
    const pairSize = this.getPairSize();
    const maxA = pairSize - this.gripSize - minB;
    clamped = Math.min(clamped, maxA);

    return clamped;
  }

  /** 父容器在当前方向上的尺寸（保留给极端 fallback） */
  private getContainerSize(): number {
    const rect = this.container.getBoundingClientRect();
    return this.direction === "horizontal" ? rect.width : rect.height;
  }

  /**
   * 当前这组 A/B 面板在主轴上的总尺寸（不含 grip）。
   * 重要：屏 4 是多分割器同容器布局，不能用 container 总宽做 clamp，
   * 必须按“当前这对面板”的可分配空间计算，否则会把另一侧面板挤到 0。
   */
  private getPairSize(): number {
    const rectA = this.panelA.getBoundingClientRect();
    const rectB = this.panelB.getBoundingClientRect();
    return this.direction === "horizontal"
      ? rectA.width + rectB.width
      : rectA.height + rectB.height;
  }

  /** grip 在当前方向上的尺寸（4px） */
  private get gripSize(): number {
    return 4;
  }

  // ===== 应用尺寸 =====

  private applySizeA(size: number): void {
    this.panelA.style.flex = "none";
    this.panelA.style.flexBasis = size + "px";
    // panelB 保持 flex:1 自动填满
  }

  private applySizes(sizeA: number, sizeB: number): void {
    // 兼容“只给 panelB 默认尺寸”的场景（例如 [0, 230]：console 固定 230，battlefield 吃剩余）
    let resolvedA = sizeA;
    if (resolvedA <= 0 && sizeB > 0) {
      const pairSize = this.getPairSize();
      // pairSize 不含 grip；A + B + grip = 可分配总长
      resolvedA = pairSize - this.gripSize - sizeB;
    }

    // fallback：极端情况下（首帧布局未稳定导致 pairSize≈0）退回 container 级估算
    if (resolvedA <= 0 && sizeB > 0) {
      const containerSize = this.getContainerSize();
      resolvedA = containerSize - this.gripSize - sizeB;
    }

    // 统一走 clamp，保证 A/B 两侧最小尺寸约束
    resolvedA = this.clampSizeA(resolvedA);
    this.applySizeA(resolvedA);
    // panelB 仍由 flex:1 自动填充
  }

  /** 获取 panelA 当前像素尺寸 */
  private getSizeA(): number {
    const rect = this.panelA.getBoundingClientRect();
    return this.direction === "horizontal" ? rect.width : rect.height;
  }

  /** 获取 panelB 当前像素尺寸 */
  private getSizeB(): number {
    const rect = this.panelB.getBoundingClientRect();
    return this.direction === "horizontal" ? rect.width : rect.height;
  }

  /**
   * 程序化设置面板尺寸（用于"折叠/展开按钮"等场景）
   * - 走 clampSizeA 保证 minSizes 约束
   * - 触发 onResize 回调（让屏 4 resizeCanvas 等订阅方同步）
   * - 不进入拖拽状态,不影响 pointer 监听
   */
  setSizes(sizes: [number, number]): void {
    this.applySizes(sizes[0], sizes[1]);
    const sizeA = this.getSizeA();
    const sizeB = this.getSizeB();
    this.onResize?.([sizeA, sizeB]);
  }

  // ===== 销毁 =====

  /** 移除所有事件监听，清理样式 */
  destroy(): void {
    // 清理全局监听（防御：即使不在拖拽中也尝试清理）
    document.removeEventListener("pointermove", this.boundOnPointerMove);
    document.removeEventListener("pointerup", this.boundOnPointerUp);

    // 清理 grip 监听
    this.grip.removeEventListener("pointerdown", this.boundOnPointerDown);
    this.grip.removeEventListener("pointerenter", this.onPointerEnter);
    this.grip.removeEventListener("pointerleave", this.onPointerLeave);

    // 清理全局样式，即使路由离开时正处于拖拽中也要恢复原值
    this.restoreBodyStyles();
    this.dragging = false;

    // 清理 rAF
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }
}
